import type { AdminExerciseRequestDetail } from '@steado/admin-contract/exercise-requests';
import { getRouteApi, useNavigate } from '@tanstack/react-router';
import { useCallback } from 'react';

import { definedSearchParams } from '@/lib/search-params';

import { ExerciseRequestsScreen } from './ExerciseRequestsScreen';
import type { ExerciseRequestsSearch } from './exercise-requests-search';

const route = getRouteApi('/exercise-requests');

export function ExerciseRequestsRoute() {
  const search = route.useSearch();
  const navigate = route.useNavigate();
  const navigateTo = useNavigate();

  const onSearchChange = useCallback(
    (next: ExerciseRequestsSearch) => {
      void navigate({ replace: true, search: () => definedSearchParams(next) });
    },
    [navigate],
  );

  /**
   * The create page reads the request itself (its draft, or its name when
   * there is none) and resolves it once the new row exists, so only the id
   * travels: a draft does not fit in a URL, and a stale copy of one should
   * not be what gets created.
   */
  const onCreateFromDraft = useCallback(
    (request: AdminExerciseRequestDetail) => {
      void navigateTo({ to: '/exercises/new', search: { exerciseRequestId: request.id } });
    },
    [navigateTo],
  );

  const onOpenExercise = useCallback(
    (exerciseId: string) => {
      // A push, not a replace: the back button returns to the queue.
      void navigateTo({ to: '/exercises/$exerciseId', params: { exerciseId }, search: { tab: 'overview' } });
    },
    [navigateTo],
  );

  return (
    <ExerciseRequestsScreen
      search={search}
      onSearchChange={onSearchChange}
      onCreateFromDraft={onCreateFromDraft}
      onOpenExercise={onOpenExercise}
    />
  );
}
